<script setup lang="ts">
import { computed } from 'vue';
import { bucketIconForTypeId } from '@/composables/modelIcons';

/**
 * A bucket type in the filter-builder dropdown: the same mark the search table
 * draws, next to its name.
 *
 * Buckets are identified by that mark everywhere else in the app and have no colour
 * pill, so picking by mark is picking by the thing you scanned the table with. It
 * resolves through `modelIconForRow`, the same function the table and the inspector
 * use, so a change to how a bucket's mark is chosen reaches all three at once.
 *
 * The mark comes from the row id, which lookupIdEntry sends as `value`. The id is
 * stable; the display name is not, and the lookup file says a row may be renamed
 * in production. Resolving from the name would blank the mark on a rename with
 * nothing to notice.
 *
 * Shown in the option rows of both dropdowns, and in the closed control of either
 * one when exactly one option is chosen.
 */
const props = defineProps<{
  value?: string;
  label?: string | null;
  /** Unused. Declared so FilterMultiCombobox's `:option` does not land in the DOM. */
  option?: unknown;
  /** Both wrappers pass "sm"; declared so it does not land in the DOM either. */
  size?: 'sm' | 'md';
}>();

const icon = computed(() => bucketIconForTypeId(props.value));
</script>

<template>
  <span class="flex items-center gap-2" data-testid="bucket-type-option">
    <component :is="icon" v-if="icon" class="size-3.5" aria-hidden="true" />
    <span>{{ label }}</span>
  </span>
</template>
